import { useMemo, useState, MouseEvent } from 'react';
import { Popover, Typography, Box, Divider, Tooltip } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { ActionIconButton } from 'components/button/ActionIconButton';
import { useStore } from 'store/store.ts';
import { OFFLINE_UNAVAILABLE_NODES, OFFLINE_UNAVAILABLE_FIELDS } from 'config/offlineRestrictions.ts';
import { getNodeConfig, getNodeColorByGroup } from 'api/workflows-steps/formSchema';
import { NodeCard } from 'views/flow/canvas/nodes/CustomNode.styles';
import { WorkflowNodeType } from 'api/model.ts';

type NodeTile = {
  type: WorkflowNodeType;
  name: string;
  icon: React.ReactElement;
  color: string;
};

const NodeIconGrid = ({ nodes }: { nodes: NodeTile[] }) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {nodes.map(({ type, name, icon, color }) => (
      <Tooltip key={type} title={name} placement="top">
        <div>
          <NodeCard className="w-8 h-8" elevation={0} color={color}>
            <span className="text-base flex items-center justify-center">{icon}</span>
          </NodeCard>
        </div>
      </Tooltip>
    ))}
  </div>
);

const OfflineModeBadge = () => {
  const offlineMode = useStore().offlineMode;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { unavailableNodes, localModelRequiredNodes } = useMemo(() => {
    const unavailable: NodeTile[] = [];
    const localModelRequired: NodeTile[] = [];
    (Object.keys(OFFLINE_UNAVAILABLE_NODES) as WorkflowNodeType[]).forEach((type) => {
      const restriction = OFFLINE_UNAVAILABLE_NODES[type];
      const nodeConfig = getNodeConfig(type);
      if (!nodeConfig) return;
      const tile: NodeTile = {
        type,
        name: nodeConfig.name,
        icon: nodeConfig.icon,
        color: getNodeColorByGroup(nodeConfig.group),
      };
      if (restriction?.availability === 'unavailable') unavailable.push(tile);
      else if (restriction?.availability === 'local-model-required') localModelRequired.push(tile);
    });
    return { unavailableNodes: unavailable, localModelRequiredNodes: localModelRequired };
  }, []);

  const fieldEntries = useMemo(() => {
    const entries: { type: WorkflowNodeType; fieldName: string; fieldTitle: string; tile: NodeTile }[] = [];
    (Object.keys(OFFLINE_UNAVAILABLE_FIELDS) as WorkflowNodeType[]).forEach((type) => {
      const fields = OFFLINE_UNAVAILABLE_FIELDS[type];
      const nodeConfig = getNodeConfig(type);
      if (!fields || !nodeConfig) return;
      const tile: NodeTile = {
        type,
        name: nodeConfig.name,
        icon: nodeConfig.icon,
        color: getNodeColorByGroup(nodeConfig.group),
      };
      Object.entries(fields).forEach(([fieldName, { title }]) =>
        entries.push({ type, fieldName, fieldTitle: title, tile }),
      );
    });
    return entries;
  }, []);

  if (!offlineMode) return null;

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <ActionIconButton
        color="warning"
        title="Offline mode is active, click for more details."
        onClick={handleOpen}
        className="ml-4"
      >
        <CloudOffIcon fontSize="small" />
      </ActionIconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              mt: 1,
              borderRadius: '16px',
              border: `1px solid ${theme.palette.divider}`,
              backgroundImage: 'none',
              boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45)',
            }),
          },
        }}
      >
        <Box className="px-6 py-5 max-w-md">
          <Typography variant="subtitle1" className="font-semibold">
            Offline mode is active
          </Typography>
          <Typography variant="body2" color="text.secondary" className="mb-3">
            The following features rely on remote services and are unavailable or require specific configuration in
            offline mode.
          </Typography>
          <Divider className="my-3" />
          {unavailableNodes.length > 0 && (
            <Box className="mb-4">
              <Typography variant="subtitle2">Unavailable nodes</Typography>
              <NodeIconGrid nodes={unavailableNodes} />
            </Box>
          )}
          {localModelRequiredNodes.length > 0 && (
            <Box className="mb-4">
              <Typography variant="subtitle2">Nodes requiring a local LLM model</Typography>
              <NodeIconGrid nodes={localModelRequiredNodes} />
            </Box>
          )}
          {fieldEntries.length > 0 && (
            <Box>
              <Typography variant="subtitle2" className="mb-2">
                Unavailable configuration fields
              </Typography>
              <div className="flex flex-col gap-2">
                {fieldEntries.map(({ type, fieldName, fieldTitle, tile }) => (
                  <div key={`${type}-${fieldName}`} className="flex items-center gap-2">
                    <Tooltip title={tile.name} placement="top">
                      <div>
                        <NodeCard className="w-8 h-8" elevation={0} color={tile.color}>
                          <span className="text-base flex items-center justify-center">{tile.icon}</span>
                        </NodeCard>
                      </div>
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                      {fieldTitle}
                    </Typography>
                  </div>
                ))}
              </div>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default OfflineModeBadge;
